import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import { useLoading } from "@/providers/loading-provider";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import { updateQuote } from "@/store/slices/quote/quote.slice";
import { apiErrorMappings } from "@/utils/errors/api-error-map";
import { trimFormValue } from "@/utils/form-input.utils";

import useSaveRequiredInformation from "./use-save-required-information";
import { useTravellersRequiredInformation } from "../provider/travellers-required-information.provider";

const useTravellersRequiredInformationHook = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const { current } = useWizard();
  const [isFooterLoading, setIsFooterLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();

  const {
    isFormComplete,
    organiserData,
    travellersData,
    organiserTravelling,
    organiserTravellerEncryptedId,
    setShowErrors,
  } = useTravellersRequiredInformation();

  const resetTrigger = useCallback(() => {
    setIsFooterLoading(false);
    hideLoading();
  }, [hideLoading]);

  const { data, error, loading, saveRequiredInformation } =
    useSaveRequiredInformation(resetTrigger);

  useEffect(() => {
    const handleEffect = async () => {
      // Handle successful data save
      if (!loading && data && !error) {
        // Update Redux state with new quote data
        dispatch(updateQuote(data));

        // Get next route from wizard definition, fallback to revision-presupuesto
        const nextRoute = current?.next || "/revision-presupuesto";

        // Navigate to next page
        router.push(nextRoute);
      }
      // Handle error cases
      else if (!loading && error !== null) {
        if (error.response?.status === 401) {
          // Unauthorized - redirect to home
          router.push("/");
        } else {
          // Show error modal
          const errorObject = await apiErrorMappings(error);
          errorObject.multiButton = true;
          dispatch(setApiError(errorObject));
        }
      }
    };
    handleEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error, dispatch, router]);

  const handleBackButton = () => {
    const prevRoute = current?.prev ?? "";
    router.push(prevRoute);
  };

  const handleContinueButton = () => {
    if (!isFormComplete) {
      setShowErrors(true);
      return;
    }

    setIsFooterLoading(true);
    showLoading();

    // Build travellers array from displayed travellers
    const travellersPayload = travellersData.map((t) => {
      const travellerPayload: {
        encryptedId: string;
        identificationType?: string;
        identificationNumber?: string;
      } = {
        encryptedId: t.encryptedId,
      };

      // Add identification fields only if BOTH are filled
      if (t.identificationType?.trim() && t.identificationNumber?.trim()) {
        travellerPayload.identificationType = t.identificationType.trim();
        travellerPayload.identificationNumber = t.identificationNumber.trim();
      }

      return travellerPayload;
    });

    // If organiser is travelling, add them to travellers with their identification
    // This ensures both organiser entity AND traveller entity (sequence 1) get updated
    if (organiserTravelling && organiserTravellerEncryptedId) {
      const organiserTravellerPayload: {
        encryptedId: string;
        identificationType?: string;
        identificationNumber?: string;
      } = {
        encryptedId: organiserTravellerEncryptedId,
      };

      // Add identification fields only if BOTH are filled
      if (
        organiserData.identificationType?.trim() &&
        organiserData.identificationNumber?.trim()
      ) {
        organiserTravellerPayload.identificationType =
          organiserData.identificationType.trim();
        organiserTravellerPayload.identificationNumber =
          organiserData.identificationNumber.trim();
      }

      travellersPayload.unshift(organiserTravellerPayload);
    }

    const payload = {
      organiser: {
        firstLineOfAddress: trimFormValue(organiserData.addressLine1),
        secondLineOfAddress: trimFormValue(organiserData.addressLine2),
        city: trimFormValue(organiserData.city),
        postCode: trimFormValue(organiserData.postalCode),
        identificationType: organiserData.identificationType,
        identificationNumber: trimFormValue(organiserData.identificationNumber),
      },
      travellers: travellersPayload,
    };

    saveRequiredInformation(payload);
  };

  return {
    isFooterLoading,
    handleBackButton,
    handleContinueButton,
  };
};

export default useTravellersRequiredInformationHook;
